import { Component, type ReactNode, StrictMode } from "react";
import { createRoot } from "react-dom/client";
import { App } from "./app";
import "./globals.css";

const rootEl = document.getElementById("root");
if (!rootEl) throw new Error("Root element not found");

function formatError(error: unknown) {
	if (error instanceof Error) {
		return error.stack ?? `${error.name}: ${error.message}`;
	}

	if (typeof error === "string") {
		return error;
	}

	try {
		return JSON.stringify(error, null, 2);
	} catch {
		return String(error);
	}
}

function FatalScreen({ title, detail }: { title: string; detail: string }) {
	return (
		<div className="flex min-h-screen items-center justify-center bg-background p-6 text-foreground">
			<div className="w-full max-w-3xl rounded-2xl border border-destructive/30 bg-card p-6 shadow-2xl">
				<h1 className="font-display text-2xl font-bold text-destructive">{title}</h1>
				<p className="mt-2 text-sm text-muted-foreground">
					The frontend hit a runtime error before it could render normally.
				</p>
				<pre className="mt-4 overflow-x-auto rounded-xl bg-black/30 p-4 font-mono text-xs text-left whitespace-pre-wrap">
					{detail}
				</pre>
			</div>
		</div>
	);
}

interface AppErrorBoundaryProps {
	children: ReactNode;
}

interface AppErrorBoundaryState {
	error: string | null;
}

class AppErrorBoundary extends Component<AppErrorBoundaryProps, AppErrorBoundaryState> {
	state: AppErrorBoundaryState = { error: null };

	static getDerivedStateFromError(error: unknown): AppErrorBoundaryState {
		return { error: formatError(error) };
	}

	componentDidCatch(error: unknown) {
		console.error("App render failed", error);
	}

	render() {
		if (this.state.error) {
			return <FatalScreen title="Frontend render failed" detail={this.state.error} />;
		}

		return this.props.children;
	}
}

const root = createRoot(rootEl);

function renderFatalScreen(title: string, error: unknown) {
	root.render(
		<StrictMode>
			<FatalScreen title={title} detail={formatError(error)} />
		</StrictMode>,
	);
}

window.addEventListener("error", (event) => {
	if (event.error) {
		renderFatalScreen("Frontend startup error", event.error);
	}
});

window.addEventListener("unhandledrejection", (event) => {
	renderFatalScreen("Unhandled promise rejection", event.reason);
});

root.render(
	<StrictMode>
		<AppErrorBoundary>
			<App />
		</AppErrorBoundary>
	</StrictMode>,
);
